import { ITherapySuppliersResult } from 'API/Suppliers/suppliers';
import {
  ICoordinate,
  ISuppliers,
  ITherapyFilters,
} from 'Context/SuppliersContext';
import { SUPPLIER_RESULT_FILTER } from 'Enum/suppliers';
import { getHaversineDistance } from 'Utils/getHaversineDistance';

export interface IAction {
  type: string;
  payload: any;
}

export function SuppliersReducer(state: ISuppliers, action: IAction) {
  switch (action.type) {
    case SUPPLIER_RESULT_FILTER.Init: {
      const results = action.payload;
      const filteredResults = action.payload;
      return { ...state, results, filteredResults };
    }
    case SUPPLIER_RESULT_FILTER.Therapy: {
      const filteredResults = TherapyFilter(
        state.results,
        action.payload.value
      );
      return { ...state, filteredResults };
    }
    default:
      return state;
  }
}

/**
 * Returns a filtered therapy suppliers array according to a given filler object.
 * Algorithm Example: [x,y,z,w] -> filter1: [x,y,z] -> filter2: [z,y] and etc.
 * @param data Therapy Suppliers Total Results Array
 * @param filters Filter Object Edited by the User
 */
function TherapyFilter(
  data: ITherapySuppliersResult[],
  filters: ITherapyFilters
) {
  const {
    supName,
    specialty,
    expertise,
    city,
    accessible,
    lng,
    gender,
    treatMethod,
    homeVisit,
    instituteId,
    orderByAscendingAlphabet,
    orderByDescendingAlphabet,
    location,
  } = filters;

  const filterByName = supName
    ? data.filter((therapyItem: ITherapySuppliersResult) =>
        therapyItem.name.includes(supName)
      )
    : data;

  const filterBySpecialty = specialty
    ? filterByName.filter((therapyItem: ITherapySuppliersResult) =>
        therapyItem.professions.includes(specialty)
      )
    : filterByName;

  const filterByExpertise =
    expertise?.length > 0
      ? filterBySpecialty.filter((therapyItem: ITherapySuppliersResult) =>
          expertise.find((expertiseId: number) =>
            therapyItem.expertises.includes(expertiseId)
          )
        )
      : filterBySpecialty;

  const filterByCity =
    city && city.length > 0
      ? filterByExpertise.filter(
          (therapyItem: ITherapySuppliersResult) =>
            (therapyItem.firstCityId &&
              city.includes(therapyItem.firstCityId)) ||
            (therapyItem.secondCityId &&
              city.includes(therapyItem.secondCityId))
        )
      : filterByExpertise;

  const filterByAccessible = accessible
    ? filterByCity.filter(
        (therapyItem: ITherapySuppliersResult) =>
          therapyItem.hasAccessibilityForHandicapped
      )
    : filterByCity;

  const filterByLanguages =
    lng && lng.length > 0
      ? filterByAccessible.filter((therapyItem: ITherapySuppliersResult) =>
          therapyItem.languages.some((languageId: number) =>
            lng.includes(languageId)
          )
        )
      : filterByAccessible;

  const filterByGender =
    gender && gender !== -1
      ? filterByLanguages.filter(
          (therapyItem: ITherapySuppliersResult) =>
            therapyItem.gender === gender
        )
      : filterByLanguages;

  const filterByTreatMethods =
    treatMethod && treatMethod.length > 0
      ? filterByGender.filter((therapyItem: ITherapySuppliersResult) =>
          therapyItem.treatmentMethods.some((treatMethodId: number) =>
            treatMethod.includes(treatMethodId)
          )
        )
      : filterByGender;

  const filterByHomeVisit = homeVisit
    ? filterByTreatMethods.filter(
        (therapyItem: ITherapySuppliersResult) => therapyItem.hasHomeVisit
      )
    : filterByTreatMethods;

  const filterByInstitute =
    instituteId !== null
      ? filterByHomeVisit.filter((therapyItem: ITherapySuppliersResult) => {
          return !!therapyItem.instituteId === instituteId;
        })
      : filterByHomeVisit;

  if (orderByAscendingAlphabet)
    filterByInstitute.sort(
      (a: ITherapySuppliersResult, b: ITherapySuppliersResult) =>
        a.name.localeCompare(b.name)
    );
  if (orderByDescendingAlphabet)
    filterByInstitute.sort(
      (a: ITherapySuppliersResult, b: ITherapySuppliersResult) =>
        b.name.localeCompare(a.name)
    );

  const filteredByLocation = location
    ? filterByInstitute.filter(
        (therapyItem: ITherapySuppliersResult) =>
          (therapyItem.firstLatitude && therapyItem.firstLongitude) ||
          (therapyItem.secondLatitude && therapyItem.secondLongitude)
      )
    : filterByInstitute;

  const filterByDistance = location
    ? filteredByLocation.sort(
        (a: ITherapySuppliersResult, b: ITherapySuppliersResult) => {
          const { coordsA, coordsB } = getShortestTwoCoordinates(
            location,
            a,
            b
          );
          if (coordsA && coordsB)
            return (
              getHaversineDistance(location, coordsA) -
              getHaversineDistance(location, coordsB)
            );
          return 0;
        }
      )
    : filteredByLocation;

  const filteredTherapy = [
    ...new Set([...filterByDistance, ...filterByInstitute]),
  ];

  return filteredTherapy;
}
/**
 * Given 2 suppliers with 4 possible coordinates return the shortest coordinate of each supplier
 * @param location User Location
 * @param a Supplier
 * @param b Supplier
 * @returns 2 Coordinates
 */
function getShortestTwoCoordinates(
  location: ICoordinate,
  a: ITherapySuppliersResult,
  b: ITherapySuppliersResult
) {
  let firstCoordsA: ICoordinate | undefined,
    secondCoordsA: ICoordinate | undefined,
    firstCoordsB: ICoordinate | undefined,
    secondCoordsB: ICoordinate | undefined;
  if (a.firstLatitude && a.firstLongitude)
    firstCoordsA = {
      latitude: a.firstLatitude,
      longitude: a.firstLongitude,
    };
  if (a.secondLatitude && a.secondLongitude)
    secondCoordsA = {
      latitude: a.secondLatitude,
      longitude: a.secondLongitude,
    };
  if (b.firstLatitude && b.firstLongitude)
    firstCoordsB = {
      latitude: b.firstLatitude,
      longitude: b.firstLongitude,
    };
  if (b.secondLatitude && b.secondLongitude)
    secondCoordsB = {
      latitude: b.secondLatitude,
      longitude: b.secondLongitude,
    };

  let coordsA: ICoordinate | undefined;
  if (firstCoordsA && secondCoordsA) {
    if (
      getHaversineDistance(location, firstCoordsA) <
      getHaversineDistance(location, secondCoordsA)
    )
      coordsA = firstCoordsA;
    else coordsA = secondCoordsA;
  }
  if (firstCoordsA) coordsA = firstCoordsA;
  if (secondCoordsA) coordsA = secondCoordsA;

  let coordsB: ICoordinate | undefined;
  if (firstCoordsB && secondCoordsB) {
    if (
      getHaversineDistance(location, firstCoordsB) <
      getHaversineDistance(location, secondCoordsB)
    )
      coordsB = firstCoordsB;
    else coordsB = secondCoordsB;
  }
  if (firstCoordsB) coordsB = firstCoordsB;
  if (secondCoordsB) coordsB = secondCoordsB;

  return { coordsA, coordsB };
}
